import { useState, useContext } from "react";
import { logActivity } from "../../../utils/LogActivity";
import { AuthContext } from "../../../Context/AuthContext";
import Preloader from "../../../shared/PreLoaders";
import { Authorization } from "../../../Context/Authorization";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s", cursor: "pointer",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };

const formatDate = (date) => {
  if (!date) return "No payment date available";
  const parsedDate = new Date(date);
  return isNaN(parsedDate) ? "Invalid Date" : parsedDate.toLocaleString("en-IN");
};

const ViewBill = () => {
  const [inputValue, setInputValue]   = useState("");
  const [searchType, setSearchType]   = useState("bill");
  const [billDetails, setBillDetails] = useState(null);
  const [repayments, setRepayments]   = useState([]);
  const [error, setError]             = useState(null);
  const [loading, setLoading]         = useState(false);
  const Api = process.env.REACT_APP_API;
  const { userId } = useContext(AuthContext);

  const handleBillSearch = async () => {
    setLoading(true);
    try {
      const config = Authorization();
      const response = await fetch(`${Api}/api/loan-collections/bill-number`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...config.headers },
        body: JSON.stringify({ billNumber: inputValue }),
      });
      if (!response.ok) throw new Error("Bill not found or an error occurred.");
      const data = await response.json();
      setBillDetails(data);
      setRepayments([]);
      await logActivity(userId, "Viewed State", "State Page", Api);
      setError(null);
    } catch (err) {
      setBillDetails(null);
      setError(err.message);
    }
    setLoading(false);
  };

  const handleLoanSearch = async () => {
    setLoading(true);
    try {
      const config = Authorization();
      const response = await fetch(`${Api}/api/loan-collections/loan-number`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...config.headers },
        body: JSON.stringify({ loanNumber: inputValue }),
      });
      if (!response.ok) throw new Error("Loan not found or an error occurred.");
      const data = await response.json();
      setRepayments(data);
      setBillDetails(null);
      await logActivity(userId, "Viewed View loan Bill", "View Bill Page", Api);
      setError(null);
    } catch (err) {
      setRepayments([]);
      setError(err.message);
    }
    setLoading(false);
  };

  const handleSearch = () => {
    if (searchType === "bill") handleBillSearch();
    else handleLoanSearch();
  };

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>View Bill</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>Loan</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>View Bill or Loan Repayments</span>
        </div>
      </div>

      {/* ── Search Card ── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
            <i className="mdi mdi-magnify" />
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Search</span>
        </div>

        {error && (
          <div style={{ marginBottom: 14, padding: "8px 14px", background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, fontSize: 13, color: "#C62828", display: "flex", alignItems: "center", gap: 8 }}>
            <i className="mdi mdi-alert-circle-outline" /> {error}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr auto", gap: 16, alignItems: "flex-end" }}>
          <div>
            <label style={labelStyle}>Search Type</label>
            <select value={searchType} onChange={(e) => setSearchType(e.target.value)} style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
              <option value="bill">Bill Number</option>
              <option value="loan">Loan Number</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>{searchType === "bill" ? "Enter Bill Number" : "Enter Loan Number"}</label>
            <input type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)}
              placeholder={searchType === "bill" ? "Enter Bill Number" : "Enter Loan Number"}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
          </div>
          <div>
            <button onClick={handleSearch}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)", whiteSpace: "nowrap" }}>
              <i className="mdi mdi-magnify" /> Search
            </button>
          </div>
        </div>
      </div>

      {/* ── Bill Details Card ── */}
      {billDetails && searchType === "bill" && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
            <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
              <i className="mdi mdi-file-document-outline" />
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Bill Details</span>
          </div>
          <div style={{ padding: "20px 24px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
              {[
                { label: "Loan ID",       value: billDetails.loanId?.loanNo },
                { label: "Sub Loan ID",   value: billDetails.subLoanId },
                { label: "Bill Number",   value: billDetails.billNumber },
                { label: "Amount Paid",   value: `₹${billDetails.amountPaid}` },
                { label: "Interest Paid", value: `₹${billDetails.interestPaid}` },
                { label: "Payment Date",  value: formatDate(billDetails.paymentDate) },
              ].map(({ label, value }) => (
                <div key={label} style={{ padding: "12px 16px", background: "#F7FAFC", borderRadius: 8, border: "1px solid #EDF2F7" }}>
                  <div style={{ fontSize: 11, color: "#718096", fontWeight: 600, marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 14, color: "#1A202C", fontWeight: 600 }}>{value || "N/A"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Repayment History Table ── */}
      {repayments.length > 0 && searchType === "loan" && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
            <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
              <i className="mdi mdi-history" />
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", flex: 1 }}>Repayment History</span>
            <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 50 }}>{repayments.length} records</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["#", "Bill Number", "Amount Paid", "Interest Paid", "Payment Date"].map((h) => (
                    <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontWeight: 600, fontSize: 12, color: "#4A5568", borderBottom: "1px solid #EDF2F7", background: "#F7FAFC", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {repayments.map((repayment, index) => (
                  <tr key={repayment._id} style={{ borderBottom: "1px solid #EDF2F7" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#F7FAFC")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                    <td style={{ padding: "10px 14px", color: "#718096" }}>{index + 1}</td>
                    <td style={{ padding: "10px 14px", color: "#1A202C", fontWeight: 600 }}>{repayment.billNumber}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ background: "#E8F5E9", color: "#2E7D32", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 50 }}>₹{repayment.amountPaid}</span>
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ background: "#E8F5E9", color: "#2E7D32", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 50 }}>₹{repayment.interestPaid}</span>
                    </td>
                    <td style={{ padding: "10px 14px", color: "#4A5568", whiteSpace: "nowrap" }}>{formatDate(repayment.paymentDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Empty State ── */}
      {!billDetails && repayments.length === 0 && !error && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#E8F5E9", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-file-search-outline" style={{ fontSize: 28, color: "#2E7D32" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>Search for a bill or loan</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Select search type, enter a number and click Search</div>
        </div>
      )}
    </div>
  );
};

export default ViewBill;
